import { computed, effectScope, onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import { useAuthStore } from '@/composition/use-auth';
import type { IReservation } from '@/interfaces/IReservation';
import { createReservationNotesQueue } from './reservation-notes-queue';
import type {
  IReservationNotesEvent,
  IReservationNotesQueue,
} from './reservation-notes-queue';

// Keep only dirty or mounted drafts. User buckets prevent another login from inheriting drafts.
const queues = new Map<number | undefined, IReservationNotesQueue>();
let observingAuthorization = false;
interface IAutosaveOptions {
  blocked: Readonly<Ref<boolean>>;
  canSave: Readonly<Ref<boolean>>;
  onSaved: (row: IReservation) => void;
  onError: (error: unknown) => void;
  queue?: IReservationNotesQueue;
}
export function useReservationNotesAutosave(
  row: Readonly<Ref<IReservation | null>>,
  options: IAutosaveOptions
) {
  const auth = useAuthStore();
  const owner = auth.user?.id;
  const authorization = () =>
    JSON.stringify([
      auth.user?.id,
      auth.currentRole?.restaurantId,
      auth.currentRole?.type,
      auth.permissions,
      auth.currentRole?.allBranches,
      auth.currentRole?.branches
        ?.map((branch) => branch.id)
        .sort((a, b) => a - b),
    ]);
  let originalAuthorization = authorization();
  if (!options.queue && !observingAuthorization) {
    observingAuthorization = true;
    // This cache outlives panels, so its privacy cleanup must outlive them too.
    effectScope(true).run(() =>
      watch(
        authorization,
        () => {
          for (const value of queues.values()) value.clear();
          queues.clear();
        },
        { flush: 'sync' }
      )
    );
  }
  function getQueue() {
    let queue = options.queue ?? queues.get(owner);
    if (!queue) {
      const permittedSession = authorization();
      queue = createReservationNotesQueue((identity, patch) => {
        if (authorization() !== permittedSession)
          return Promise.reject(
            new Error('Reservation draft belongs to another session')
          );
        return ReservationsService.update(identity.id, patch);
      });
      queues.set(owner, queue);
    }
    return queue;
  }
  let persistence = getQueue();
  const key = ref<string | null>(null);
  const revision = ref(0);
  const touched = new Set<string>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const waiting = new Set<(ready: boolean) => void>();
  let alive = true;
  function onEvent(event: IReservationNotesEvent) {
    if (!alive || !touched.has(event.key)) return;
    revision.value++;
    if (event.kind === 'saved') options.onSaved(event.row);
    if (event.kind === 'error') options.onError(event.error);
  }
  let unsubscribe = persistence.subscribe(onEvent);
  const state = computed(() => {
    revision.value;
    return persistence.snapshot(key.value);
  });
  const notes = computed(() => state.value?.notes ?? '');
  const tags = computed(() => state.value?.tags ?? []);
  const dirty = computed(() => {
    revision.value;
    return [...touched].some((value) => persistence.snapshot(value)?.dirty);
  });
  const saving = computed(() => {
    revision.value;
    return [...touched].some((value) => persistence.snapshot(value)?.saving);
  });
  const error = computed(() => {
    revision.value;
    return [...touched].some((value) => !!persistence.snapshot(value)?.error);
  });
  function clearTimer(value: string) {
    clearTimeout(timers.get(value));
    timers.delete(value);
  }
  async function ready() {
    if (!alive || !options.canSave.value) return false;
    if (!options.blocked.value) return true;
    return new Promise<boolean>((resolve) => {
      waiting.add(resolve);
    });
  }
  watch([options.blocked, options.canSave], () => {
    if (options.blocked.value && options.canSave.value) return;
    for (const resolve of waiting) resolve(alive && options.canSave.value);
    waiting.clear();
  });
  async function flush(): Promise<boolean> {
    for (const value of timers.keys()) clearTimer(value);
    if (!dirty.value) return true;
    if (!(await ready())) return false;
    return persistence.flushAll(touched);
  }
  async function afterFlush(
    work: () => void | Promise<void>
  ): Promise<boolean> {
    const current = key.value;
    if (!(await flush()) || !alive || current !== key.value) return false;
    await work();
    return true;
  }
  watch(
    [authorization, options.canSave],
    () => {
      if (authorization() === originalAuthorization && options.canSave.value) {
        if (!key.value && auth.user?.id === owner) bindRow(row.value);
        return;
      }
      unsubscribe();
      persistence.clear();
      for (const value of timers.keys()) clearTimer(value);
      for (const resolve of waiting) resolve(false);
      waiting.clear();
      touched.clear();
      key.value = null;
      originalAuthorization = authorization();
      persistence = getQueue();
      unsubscribe = persistence.subscribe(onEvent);
      if (options.canSave.value && auth.user?.id === owner) bindRow(row.value);
      revision.value++;
    },
    { flush: 'sync' }
  );
  function edit(value: { notes?: string; tags?: string[] }) {
    const active = key.value;
    if (!alive || !options.canSave.value || !active) return;
    persistence.edit(active, value);
    clearTimer(active);
    timers.set(
      active,
      setTimeout(() => {
        timers.delete(active);
        if (alive) void flush();
      }, 700)
    );
  }
  function bindRow(value: IReservation | null) {
    const old = key.value;
    if (
      value &&
      old === `${value.restaurantId}:${value.branchId}:${value.id}`
    ) {
      persistence.accept(value);
      return;
    }
    if (old) {
      clearTimer(old);
      // Direct prop/store changes cannot be blocked; keep the captured reservation's queue.
      if (options.canSave.value) void persistence.flush(old);
      persistence.release(old);
    }
    key.value =
      value && options.canSave.value && auth.user?.id === owner
        ? persistence.bind(value)
        : null;
    if (key.value) touched.add(key.value);
    revision.value++;
  }
  watch(row, bindRow, { immediate: true, flush: 'sync' });
  onScopeDispose(() => {
    alive = false;
    unsubscribe();
    for (const value of timers.keys()) clearTimer(value);
    for (const resolve of waiting) resolve(false);
    waiting.clear();
    // Flush captured edits without updating a destroyed view. Failures remain available on remount.
    if (options.canSave.value) void persistence.flushAll(touched);
    if (key.value) persistence.release(key.value);
  });
  return {
    notes,
    tags,
    dirty,
    saving,
    error,
    flush,
    afterFlush,
    setNotes: (value: string) => edit({ notes: value }),
    setTags: (value: string[]) => edit({ tags: value }),
  };
}
